import type { TFunction } from 'i18next';
import { z } from 'zod';

import {
	ParentHcoSchema as GeneratedParentHcoSchema,
	HcoSchema as GeneratedHcoSchema
} from '@/core/graphql/generated/validation.zod';

const cleanString = (v: string | null | undefined) => (v ?? '').trim();

export const AffiliationRowSchema = (t?: TFunction) => {
	const relationshipRaw = GeneratedParentHcoSchema().pick({
		vid_V: true,
		jobTitle_V: true,
		classOfWork_V: true,
		isPrimaryRelationship_V: true
	});

	const hcoRaw = GeneratedHcoSchema().pick({
		hcoNameCda_V: true,
		statusCda_V: true,
		hcoNameIntl_V: true
	});

	const requiredMsg = t
		? t('This field is required')
		: 'This field is required';

	return z.object({
		vid_V: z.string().nullish(),

		jobTitle_V: relationshipRaw.shape.jobTitle_V
			.nullish()
			.transform(cleanString),
		typeOfRelationship_VId: z.string().nullish().transform(cleanString),
		classOfWork_V: relationshipRaw.shape.classOfWork_V
			.nullish()
			.transform(cleanString),
		isPrimaryRelationship_V: relationshipRaw.shape.isPrimaryRelationship_V
			.nullish()
			.transform(v => !!v),

		parentHcoVid: z.string().nullish().transform(cleanString),

		hcoNameCda_V: z
			.string({ error: requiredMsg })
			.trim()
			.min(1, { message: requiredMsg }),
		hcoNameIntl_V: hcoRaw.shape.hcoNameIntl_V.nullish().transform(cleanString),
		hcoType: z.string().optional(),
		hcoStatus: hcoRaw.shape.statusCda_V.nullish().transform(cleanString),

		addresses: z.array(z.record(z.string(), z.any())).optional().default([]),

		location: z.string().nullish()
	});
};

export const AffiliationsSectionSchema = (t?: TFunction) => {
	return z.object({
		parenthcos: z.array(AffiliationRowSchema(t)),
		partnerhcos: z.array(AffiliationRowSchema(t))
	});
};

export type AffiliationRowValuesIn = z.input<
	ReturnType<typeof AffiliationRowSchema>
>;
export type AffiliationsSectionValuesIn = z.input<
	ReturnType<typeof AffiliationsSectionSchema>
>;
